import { EvmPriceServiceConnection } from '@pythnetwork/pyth-evm-js'
import { BigNumber } from 'utils/ethersImport'
import { ENABLE_LOGGING } from 'utils/constants'
import { create } from 'zustand'
import { devtools, subscribeWithSelector } from 'zustand/middleware'
import { getActionName } from './utils'
import { TOKENS } from 'config/tokens'
import { DEFAULT_CHAIN_ID } from 'config/chains'
import map from 'lodash/map'

export type TokenPriceInfo = {
  minPrice: BigNumber
  maxPrice: BigNumber
  conf: BigNumber
  ts: number
}

const STORE_NAME = 'prices'

export type PriceStoreType = {
  prices: Record<string, TokenPriceInfo>
  setTokenPrice: (tokenAddresses: string, price: TokenPriceInfo) => void
  setSpreadReductionBps: (spreadReductionBps: Record<string, number>) => void
  spreadReductionBps: Record<string, number>
  setPricesUsingApi: (prices: PriceStoreType['prices']) => void
  isWSBlocked: boolean
  setIsWSBlocked: (isWSBlocked: boolean) => void
  pythIdsForWS: string[]
  setPythIdForWS: (pythIdForWS: string | string[]) => void
  removePythIdForWS: (pythIdForWS: string | string[]) => void
  wsConnectionInfo: {
    ref: EvmPriceServiceConnection | null
    updatedAt: Date
  }
  setWsConnectionInfo: (
    wsConnectionRef: PriceStoreType['wsConnectionInfo'],
  ) => void
}

const priceStore = create<PriceStoreType>()(
  devtools(
    subscribeWithSelector((set) => ({
      prices: {},
      setTokenPrice(tokenAddress, priceInfo) {
        set(
          ({ prices }) => {
            const currentPriceInfo = prices[tokenAddress] as
              | TokenPriceInfo
              | undefined

            const shouldUpdate =
              priceInfo.ts - (currentPriceInfo?.ts || 0) > 500

            if (shouldUpdate) {
              return {
                prices: {
                  ...prices,
                  [tokenAddress]: priceInfo,
                },
              }
            }
            return {}
          },
          false,
          getActionName(STORE_NAME, 'setTokenPrice'),
        )
      },
      spreadReductionBps: (() => {
        const pythIdsForWS = map(TOKENS[DEFAULT_CHAIN_ID], 'pythId')
        const spread: Record<string, number> = {}
        for (let i = 0; i < pythIdsForWS.length; i++) {
          const pythId = pythIdsForWS[i]
          if (!pythId) {
            continue
          }
          spread[pythId] = 9999
        }
        return spread
      })(),
      setSpreadReductionBps(spreadReductionBps) {
        set(
          { spreadReductionBps },
          false,
          getActionName(STORE_NAME, 'setSpreadReductionBps'),
        )
      },
      isWSBlocked: true,
      setIsWSBlocked(isWSBlocked) {
        set({ isWSBlocked }, false, getActionName(STORE_NAME, 'setIsWSBlocked'))
      },
      setPricesUsingApi(pricesFromApi) {
        set(
          ({ prices }) => ({
            prices: {
              ...prices,
              ...pricesFromApi,
            },
          }),
          false,
          getActionName(STORE_NAME, 'setPricesUsingApi'),
        )
      },
      wsConnectionInfo: {
        ref: null,
        updatedAt: new Date(),
      },
      setWsConnectionInfo(wsConnectionInfo) {
        set(
          { wsConnectionInfo },
          false,
          getActionName(STORE_NAME, 'setWsConnectionInfo'),
        )
      },
      pythIdsForWS: [],
      setPythIdForWS(pythId) {
        set(({ pythIdsForWS }) => {
          if (pythId && !pythIdsForWS.some((id) => id === pythId)) {
            return {
              pythIdsForWS:
                typeof pythId === 'object'
                  ? [...pythIdsForWS, ...pythId]
                  : [...pythIdsForWS, pythId],
            }
          }
          return {}
        })
      },
      removePythIdForWS(pythId) {
        set(
          ({ pythIdsForWS }) => ({
            pythIdsForWS: pythIdsForWS.filter((id) => id !== pythId),
          }),
          false,
          getActionName(STORE_NAME, 'removePythIdForWS'),
        )
      },
    })),
    { enabled: ENABLE_LOGGING, name: 'prices' },
  ),
)

export default priceStore
